import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { FileText, Calendar, Phone, Users, Clock } from 'lucide-react';
import { toast } from 'sonner';

const DailyReportsPage = () => {
  const { api } = useAuth();
  const [reports, setReports] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('');
  const [userFilter, setUserFilter] = useState('all');

  useEffect(() => {
    fetchReports();
    fetchExecutives();
  }, [dateFilter, userFilter]);

  const fetchExecutives = async () => {
    try {
      const response = await api.get('/users/executives');
      setExecutives(response.data || []);
    } catch (err) {
      console.error('Failed to load executives');
    }
  };

  const fetchReports = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilter) params.append('date', dateFilter);
      if (userFilter && userFilter !== 'all') params.append('userId', userFilter);

      const response = await api.get(`/daily-reports?${params.toString()}`);
      setReports(response.data || []);
    } catch (err) {
      toast.error('Failed to load daily reports');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="daily-reports-loading">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="daily-reports-page">
      <div>
        <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
          Daily Reports
        </h1>
        <p className="text-muted-foreground">
          End-of-day summaries from executives
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div>
          <Label htmlFor="dateFilter">Date</Label>
          <Input
            id="dateFilter"
            type="date"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="w-[180px]"
            data-testid="date-filter"
          />
        </div>
        <div>
          <Label htmlFor="userFilter">Executive</Label>
          <Select value={userFilter} onValueChange={setUserFilter}>
            <SelectTrigger className="w-[200px]" data-testid="user-filter">
              <SelectValue placeholder="All Executives" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Executives</SelectItem>
              {executives.map((exec) => (
                <SelectItem key={exec.id} value={exec.id}>{exec.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {reports.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No reports found</h3>
            <p className="text-muted-foreground">Daily reports will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {reports.map((report) => (
            <Card key={report.id} className="card-hover" data-testid={`report-${report.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{report.userName}</CardTitle>
                  <Badge variant="secondary">
                    <Calendar className="mr-1 h-3 w-3" />
                    {report.date}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="mb-4">{report.summary}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span><strong>{report.callsMade}</strong> calls</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <span><strong>{report.leadsContacted}</strong> contacted</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span><strong>{report.meetingsScheduled}</strong> meetings</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    <span>{new Date(report.createdAt).toLocaleTimeString()}</span>
                  </div>
                </div>
                {report.remarks && (
                  <p className="mt-4 text-sm text-muted-foreground border-t pt-4">
                    {report.remarks}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DailyReportsPage;
